import React, { useEffect, useState } from "react";
import { EmptyState } from "../../../../../components/EmptyState/EmptyState";
import {
  ACTIVITIES_PERFORMED_NAME_LABEL,
  CUSTOMER_NAME_LABEL,
  DELETE_PROJECT_CONFIRM_MESSAGE,
  DELETE_PROJECT_LABEL,
  DESCRIPTION_LABEL,
  EMPTY_PROJECTS_MESSAGE,
  EMPTY_STATE_PROJECTS_COLLABORATOR_MESSAGE,
  ENDING_DATE_LABEL,
  NAME_LABEL,
  PROJECT_LABEL,
  PROJECTS_LABEL,
  RESOURSES_POOL_NAME_LABEL,
  STARTING_DATE_LABEL,
} from "../../../../../constants/Dictionary";
import emptyProjects from "../../../../../images/projects.png";
import {
  Button,
  ButtonVariants,
} from "../../../../../components/core/Button/Button";
import { ReactComponent as AddIcon } from "../../../../../icons/add.svg";
import { CollaboratorHistoryProject } from "../../../../../types/api/projects.type";
import { FiltersPanel } from "../../../../../components/FiltersPanel/FiltersPanel";
import { DoubleStructure } from "../../../../../components/core/DoubleStructure/DoubleStructure";
import { BodyTyp } from "../../../../../components/Typography/Typography";
import {
  AlignmentEnum,
  FilterProps,
  FontWeightEnum,
} from "../../../../../types/AppTypes";
import SearchInput from "../../../../../components/core/SearchInput/SearchInput";
import { StyledExpandTable } from "../ProfessionalHistory.styled";
import { Loader } from "../../../../../components/core/Loader/Loader";
import {
  getCollaboratorProjectsColumns,
  shortDateFormat,
} from "../../../../../constants/TableColumns";
import { Link, LinkSizeEnum } from "../../../../../components/core/Link/Link";
import { PopupConfirm } from "../../../../../components/PopupConfirm/PopupConfirm";
import { CollaboratorProjectModal } from "./CollaboratorProjectModal/CollaboratorProjectModal";
import { COLLABORATORS_PAGE_SIZE } from "../../../../../constants/AppConstants";
import { useDebounce } from "use-debounce";
import { useAppSelector } from "../../../../../hooks/store.hook";
import { FiltersSelected } from "../../../../../components/FiltersSelected/FiltersSelected";
import {
  Table,
  TableThemeEnum,
} from "../../../../../components/core/Table/Table";
import LoadingThumbnail from "../../../../../components/LoadingThumbnail/LoadingThumbnail";
import {
  TextBox,
  TextBoxGrid,
} from "../../../../../components/TextBoxGrid/TextBoxGrid.component";
import { getCollaboratorHistoryProjectsFiltersTitles } from "../../../../../constants/FilterTitles";
import {
  CollaboratorCmSheet,
  CollaboratorDetails,
} from "../../../../../types/api/collaborators.type";
import { canEditCollaboratorHistory } from "../../../../../utils/canEditCollaboratorHistory.utils";
import {
  useHistoryProjectFilters,
  useHistoryProjects,
  useHistoryProjectDelete,
} from "../../../../../hooks/projectsHistory.hook";

type ProjectsHistoryProps = {
  setLink: (component: React.ReactNode) => void;
  collaborator: CollaboratorDetails;
  onStatusRefresh: () => void;
  isMyProfile?: boolean;
  currentCmSheet?: CollaboratorCmSheet;
};

export const ProjectsHistory = ({
  setLink,
  collaborator,
  onStatusRefresh,
  isMyProfile,
  currentCmSheet,
}: ProjectsHistoryProps) => {
  const [selectedProject, setSelectedProject] = useState<
    CollaboratorHistoryProject | undefined
  >(undefined);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [searchDebouncedValue] = useDebounce(searchValue, 500);
  const [pageNumber, setPageNumber] = useState(0);
  const [filters, setFilters] = useState<FilterProps[]>([]);

  const { profile } = useAppSelector((state) => state.user);
  const profileId = collaborator.id;
  const [hasData, setHasData] = useState<boolean | null>(null);

  const { data: filtersData, isLoading: filtersLoading } =
    useHistoryProjectFilters(profileId);

  const joinFilters = Object.values(filtersData ?? []).flat();

  const { data: historyProjects, isFetching: projectFetching } =
    useHistoryProjects({
      profileId,
      pageNumber,
      searchDebouncedValue,
      filters,
    });
  const {
    mutateAsync: historyProjectDelete,
    isPending: pendingHistoryProjectDelete,
  } = useHistoryProjectDelete(profileId);

  const handleFilters = (filters: FilterProps[]) => {
    setPageNumber(0);
    setFilters(filters);
  };

  const onDelete = (record: CollaboratorHistoryProject) => {
    setSelectedProject(record);
    setShowDeletePopup(true);
  };

  const deleteProject = async () => {
    await historyProjectDelete(selectedProject?.projectId?.toString()!);
    hideDeletePopup();
    onStatusRefresh();
  };

  const hideDeletePopup = () => {
    setSelectedProject(undefined);
    setShowDeletePopup(false);
  };

  const hideAddModal = () => {
    setSelectedProject(undefined);
    setShowProjectModal(false);
  };

  const handleSave = async (currentHasData: boolean | null) => {
    hideAddModal();
    if (currentHasData === false) {
      setHasData(null);
    }
    onStatusRefresh();
  };

  const onEdit = (record: CollaboratorHistoryProject) => {
    setSelectedProject(record);
    setShowProjectModal(true);
  };

  const rowKey = (record: CollaboratorHistoryProject) => {
    return record.projectId.toString();
  };

  const canEditHistory = canEditCollaboratorHistory(
    profile,
    collaborator,
    currentCmSheet,
    isMyProfile,
  );

  useEffect(() => {
    const handleLink = () => {
      if (!canEditHistory) return;
      setLink(
        <Link
          onClick={() => setShowProjectModal(true)}
          icon={<AddIcon />}
          size={LinkSizeEnum.SMALL}
        >
          {PROJECT_LABEL}
        </Link>,
      );
    };

    const updateHasData = () => {
      if (!projectFetching && historyProjects && hasData === null) {
        const result = historyProjects.pagination.total > 0;
        setHasData(result);
      }
    };
    hasData ? handleLink() : updateHasData();

    return () => setLink(null);
  }, [historyProjects, hasData, projectFetching, canEditHistory]);

  const filtersAreActive = filters.length > 0;

  useEffect(() => {
    setPageNumber(0);
  }, [searchDebouncedValue]);

  const expandedRow = (
    record: CollaboratorHistoryProject,
  ): React.ReactElement | null => {
    return (
      <>
        <TextBoxGrid
          numCol={3}
          items={[
            {
              label: CUSTOMER_NAME_LABEL,
              text: record.projectItem.customer,
            },
            {
              label: STARTING_DATE_LABEL,
              text: shortDateFormat(record.projectItem.startDate),
            },
            {
              label: ENDING_DATE_LABEL,
              text: shortDateFormat(record.projectItem.endDate),
            },
          ]}
        />
        <TextBoxGrid
          numCol={3}
          items={[
            {
              label: ACTIVITIES_PERFORMED_NAME_LABEL,
              text: record.projectItem.activitiesPerformed,
            },
            {
              label: RESOURSES_POOL_NAME_LABEL,
              text: record.projectItem.resourcesPool,
            },
          ]}
        />
        <TextBox
          label={DESCRIPTION_LABEL}
          text={record.projectItem.projectDescription}
        />
      </>
    );
  };

  const showEmptyState = !hasData && !projectFetching;
  const showLoader = projectFetching && hasData === null;

  return (
    <div className="content">
      {showLoader ? (
        <Loader loading={true}>
          <LoadingThumbnail marginTop={416} />
        </Loader>
      ) : showEmptyState ? (
        <div className="empty-state">
          <EmptyState
            label={EMPTY_STATE_PROJECTS_COLLABORATOR_MESSAGE}
            imageSrc={emptyProjects}
          >
            {canEditHistory && (
              <Button
                label={PROJECT_LABEL}
                variant={ButtonVariants.SECONDARY}
                icon={<AddIcon />}
                onClick={() => setShowProjectModal(true)}
              />
            )}
          </EmptyState>
        </div>
      ) : (
        <>
          <DoubleStructure verticalAlignment={AlignmentEnum.CENTER}>
            <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
              {!projectFetching &&
                `${historyProjects?.pagination.total} ${
                  historyProjects?.data.length === 1
                    ? PROJECT_LABEL
                    : PROJECTS_LABEL
                }`}
            </BodyTyp>
            <div className="filters">
              <SearchInput
                onChange={setSearchValue}
                label={NAME_LABEL}
                name={"search"}
                value={searchValue}
              />
              <FiltersPanel
                data={filtersData}
                filtersTitles={getCollaboratorHistoryProjectsFiltersTitles()}
                setSelectedFilters={handleFilters}
                selectedFilters={filters}
                loading={filtersLoading}
                defaultExpanded
              />
            </div>
          </DoubleStructure>
          <div className="active-filters">
            <FiltersSelected
              data={joinFilters}
              filters={filters}
              setFilters={handleFilters}
            />
          </div>
          <StyledExpandTable>
            <Table<CollaboratorHistoryProject>
              theme={TableThemeEnum.DARK}
              loading={projectFetching}
              rowKey={rowKey}
              headers={getCollaboratorProjectsColumns}
              scrollY={`calc(100dvh - ${filtersAreActive ? "532px" : "502px"})`}
              onEdit={canEditHistory ? onEdit : undefined}
              onDelete={canEditHistory ? onDelete : undefined}
              data={historyProjects?.data || []}
              pagination={{
                current: pageNumber + 1,
                onChange: (pageNumber) => setPageNumber(pageNumber - 1),
                total: historyProjects?.pagination.total ?? 0,
                pageSize: COLLABORATORS_PAGE_SIZE,
              }}
              expandable={{
                expandedRowRender: expandedRow,
              }}
              emptyText={EMPTY_PROJECTS_MESSAGE}
            />
          </StyledExpandTable>
        </>
      )}
      <PopupConfirm
        isOpen={showDeletePopup}
        label={DELETE_PROJECT_LABEL}
        popupText={`${DELETE_PROJECT_CONFIRM_MESSAGE} ${selectedProject?.projectItem.name}?`}
        onConfirm={deleteProject}
        onCancel={hideDeletePopup}
        isLoading={pendingHistoryProjectDelete}
      />
      {showProjectModal && (
        <CollaboratorProjectModal
          isOpen={showProjectModal}
          onSave={() => handleSave(hasData)}
          onClose={() => hideAddModal()}
          {...(selectedProject && {
            record: selectedProject,
          })}
          collaborator={collaborator}
        />
      )}
    </div>
  );
};
